import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, CalendarX, Users, Plus } from "lucide-react";
import { toast } from "sonner";

function MonthRow({ m, onDelete }) {
  return (
    <div data-testid={`menu-month-${m.key}`}
      className="flex items-center justify-between px-3 py-1.5 border-b last:border-0 border-slate-100 text-sm">
      <span className="font-mono font-semibold">{m.key} <span className="text-slate-400 font-normal">({m.num_days} gg)</span></span>
      <button data-testid={`menu-month-delete-${m.key}`} onClick={() => onDelete(m.key)}
        className="text-slate-400 hover:text-red-700 transition-colors">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function MonthsSection({ months, onChanged }) {
  const deleteMonth = async (key) => {
    if (!window.confirm(`Eliminare definitivamente il mese ${key} con tutta la sua programmazione?`)) return;
    try {
      await api.delete(`/months/${encodeURIComponent(key)}`);
      toast.success(`Mese ${key} eliminato`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione mese"); }
  };

  return (
    <div className="border border-slate-300 rounded-sm">
      <div className="bg-slate-100 px-3 py-2 border-b border-slate-300 flex items-center gap-2">
        <CalendarX className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-bold uppercase tracking-tight">Dati obsoleti — mesi</span>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {months.length === 0 && <p className="text-xs text-slate-400 italic px-3 py-2">Nessun mese presente</p>}
        {months.map((m) => <MonthRow key={m.key} m={m} onDelete={deleteMonth} />)}
      </div>
    </div>
  );
}

function OperatorRow({ o, isAdmin, onDelete }) {
  return (
    <div data-testid={`menu-operator-${o.name}`}
      className="flex items-center justify-between px-3 py-1.5 border-b last:border-0 border-slate-100 text-sm">
      <span>{o.name} {o.email && <span className="text-xs text-slate-400">({o.email})</span>} {!o.has_password && <span className="text-xs text-amber-700">(non autorizzato)</span>}</span>
      {isAdmin && (
        <button data-testid={`menu-operator-delete-${o.name}`} onClick={() => onDelete(o.name)}
          className="text-slate-400 hover:text-red-700 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function OperatorForm({ onSave }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const submit = async () => {
    if (await onSave(name, password)) { setName(""); setPassword(""); }
  };
  return (
    <div className="p-2 border-t border-slate-200 space-y-2">
      <p className="text-[11px] text-slate-500">Autorizza un operatore assegnandogli nome e password univoca:</p>
      <div className="flex gap-2">
        <Input data-testid="menu-operator-name-input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Nome operatore" className="rounded-sm h-8 text-sm"
          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
        <Input data-testid="menu-operator-password-input" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="new-password"
          className="rounded-sm h-8 text-sm" />
        <Button data-testid="menu-operator-add-button" size="sm"
          className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white h-8 shrink-0" onClick={submit}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function useOperators(open) {
  const [operators, setOperators] = useState([]);

  const loadOperators = useCallback(async () => {
    try {
      const res = await api.get("/operators");
      setOperators(res.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("Caricamento operatori fallito", err);
      toast.error("Errore caricamento operatori");
    }
    // api è un import di modulo stabile
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) loadOperators();
  }, [open, loadOperators]);

  const saveOperator = async (name, password) => {
    if (!name.trim() || password.length < 4) { toast.error("Nome richiesto e password di almeno 4 caratteri"); return false; }
    try {
      await api.post("/operators", { name, password });
      toast.success(`Operatore "${name.trim()}" autorizzato`);
      loadOperators();
      return true;
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio operatore"); return false; }
  };

  const deleteOperator = async (name) => {
    if (!window.confirm(`Eliminare l'operatore "${name}"?`)) return;
    try {
      await api.delete(`/operators/${encodeURIComponent(name)}`);
      setOperators((cur) => cur.filter((o) => o.name !== name));
      toast.success(`Operatore ${name} eliminato`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione operatore"); }
  };

  return { operators, saveOperator, deleteOperator };
}

function OperatorsSection({ open }) {
  const { isAdmin } = useAuth();
  const { operators, saveOperator, deleteOperator } = useOperators(open);

  return (
    <div className="border border-slate-300 rounded-sm">
      <div className="bg-slate-100 px-3 py-2 border-b border-slate-300 flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-bold uppercase tracking-tight">Operatori registrati</span>
      </div>
      <div className="max-h-40 overflow-y-auto">
        {operators.map((o) => <OperatorRow key={o.name} o={o} isAdmin={isAdmin} onDelete={deleteOperator} />)}
      </div>
      {isAdmin ? <OperatorForm onSave={saveOperator} /> : (
        <p className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-200">
          Solo l'amministratore (Tiralongo Matteo) può autorizzare o eliminare operatori.
        </p>
      )}
    </div>
  );
}

export default function GeneralMenuDialog({ open, onClose, months, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="general-menu-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight">Menu generale</DialogTitle>
        </DialogHeader>
        <MonthsSection months={months} onChanged={onChanged} />
        <OperatorsSection open={open} />
      </DialogContent>
    </Dialog>
  );
}
